/**
 * Build a mock menu item (defaults: available, not favorite, not a setting).
 * @param {string} id
 * @param {string} label
 * @param {Partial<ApiManagerMenuItem> & Partial<ApiManagerMenuItemConfig>} [overrides]
 * @returns {ApiManagerMenuItem}
 */
const menuItem = (id, label, {
  setting = false, available = true, favorite = false, ...rest
} = {}) => ({
  id,
  icon: null,
  label,
  path: rest.items?.length ? null : `/${id}`,
  description: null,
  settingsDescription: null,
  config: { setting, available, favorite },
  items: null,
  ...rest,
});

/**
 * @param {string} prefix
 * @param {string[]} labels
 * @param {Partial<ApiManagerMenuItem> & Partial<ApiManagerMenuItemConfig>} [overrides]
 * @returns {ApiManagerMenuItem[]}
 */
const leaves = (prefix, labels, overrides = {}) => labels.map(
  (label, index) => menuItem(`${prefix}-${index + 1}`, label, overrides),
);

/** L2 item with a full popover (L3 links, L3 groups, Configuration zone), from Figma 7634-160245. */
const accueilArtiste = menuItem('evenements-accueil', 'Accueil artiste', {
  description: 'Lorem ipsum dolor sit amet consectetur.',
  settingsDescription: 'Lorem ipsum dolor sit amet consectetur. Donec bibendum viverra eget nisi sit nunc et dolor in.',
  items: [
    menuItem('accueil-tableau-de-bord', 'Tableau de bord', { favorite: true }),
    menuItem('accueil-contacts', 'Contacts'),
    menuItem('accueil-planning', 'Planning des accueils', { favorite: true }),
    menuItem('accueil-technique', 'Technique'),
    menuItem('accueil-fichiers', 'Fichiers', { available: false }),
    menuItem('accueil-horaires', 'Horaires', {
      items: leaves('accueil-horaires', ['Liste', 'Horaires de chantiers', 'Horaires par lieux']),
    }),
    menuItem('accueil-riders', 'Etudes riders', {
      items: leaves('accueil-riders', ['Listes', 'Total par types et par dates', 'Listes par artistes et par types']),
    }),
    menuItem('accueil-contact', 'Contact', {
      items: [
        menuItem('accueil-contact-details', 'Détails', { favorite: true }),
        menuItem('accueil-contact-deals', 'Deals'),
        menuItem('accueil-contact-embauches', 'Embauches'),
      ],
    }),
    menuItem('accueil-formulaires', 'Formulaires', {
      items: leaves('accueil-formulaires', ['Listes', 'Résumé']),
    }),
    menuItem('accueil-restauration', 'Restauration', {
      items: leaves('accueil-restauration', [
        'Services', 'Couverts', 'Couverts par groupes', 'Négociations et suivis', 'Par artistes', 'Par bénéficiaires',
      ]),
    }),
    menuItem('accueil-hebergements', 'Hébergements', {
      items: leaves('accueil-hebergements', ['Liste', 'Négociations et suivis', 'Quotas', 'Quota par date']),
    }),
    menuItem('accueil-parametres', 'Paramètres', {
      items: leaves('accueil-parametres', ['Catégories des bénéficiaires', 'Conditions d\'accueil']),
    }),
    menuItem('accueil-transport', 'Transport', {
      items: [
        menuItem('accueil-transport-liste', 'Liste'),
        menuItem('accueil-transport-negociations', 'Négociations et suivis', { available: false }),
      ],
    }),
    menuItem('accueil-config-horaires', 'Horaires', {
      setting: true,
      items: leaves('accueil-config-horaires', ['Catégories', 'Horaires récurrents', 'Favoris', 'Activer l\'heure annoncée']),
    }),
    menuItem('accueil-config-transports', 'Transports', {
      setting: true,
      items: leaves('accueil-config-transports', ['Modes de transport', 'Suivis des transports', 'Suivis des transferts', 'Favoris']),
    }),
    menuItem('accueil-config-hebergements', 'Hébergements', {
      setting: true,
      items: leaves('accueil-config-hebergements', ['Prestataires', 'Prestations', 'Tarifs par périodes', 'Suivi des hébergements']),
    }),
    menuItem('accueil-config-restauration', 'Restauration', {
      setting: true,
      items: leaves('accueil-config-restauration', ['Prestataires', 'Favoris']),
    }),
    menuItem('accueil-config-riders', 'Etudes riders', {
      setting: true,
      items: leaves('accueil-config-riders', ['Catégories', 'Types', 'Références', 'Suivis']),
    }),
  ],
});

/** @type {ApiManagerMenuItem[]} */
const mockMenuItems = [
  menuItem('evenements', 'Événements', {
    icon: 'fal fa-calendar-star',
    items: [
      menuItem('evenements-liste', 'Liste des événements', { favorite: true }),
      accueilArtiste,
      menuItem('evenements-planning', 'Planning'),
    ],
  }),
  menuItem('rh', 'RH & Staff', {
    icon: 'fal fa-user-tie',
    items: [
      ...leaves('rh', ['Staff', 'Contrats', 'Plannings staff']),
      // Forbidden for the user: only listed in the menu search, with a lock
      menuItem('rh-paie', 'Paie', {
        path: null,
        config: {
          setting: false, available: true, favorite: false, allowed: false,
        },
      }),
    ],
  }),
  menuItem('studios', 'Studios, Salles', {
    icon: 'fal fa-building',
    items: leaves('studios', ['Salles', 'Réservations']),
  }),
  menuItem('planning', 'Planning, Agenda', {
    icon: 'fal fa-book-open',
    items: leaves('planning', ['Agenda', 'Timeline']),
  }),
  menuItem('contacts', 'Contacts', {
    icon: 'fal fa-address-book',
    items: leaves('contacts', ['Personnes', 'Structures']),
  }),
  menuItem('artistes', 'Artistes, Cies, Ateliers', {
    icon: 'fal fa-user',
    items: leaves('artistes', ['Artistes', 'Compagnies', 'Ateliers']),
  }),
  menuItem('outils', 'Outils bureautiques', {
    icon: 'fal fa-toolbox',
    available: false,
    items: leaves('outils', ['Exports', 'Imports'], { available: false }),
  }),
];

/**
 * @type {ApiManagerLayout}
 */
export const mockManagerLayout = {
  user: {
    avatar: '',
    notificationCount: 2,
  },
  menu: {
    items: mockMenuItems,
    bottomSection: {
      items: [
        menuItem('aide', 'Aide', {
          icon: 'fal fa-circle-question',
          items: leaves('aide', ['Documentation', 'Contacter le support']),
        }),
        menuItem('deconnexion', 'Déconnexion', { icon: 'fal fa-arrow-right-from-bracket' }),
      ],
    },
  },
  // Real "Programmation" breadcrumb: settings entries (`config.setting`) go to the Configuration zone
  breadcrumb: [
    menuItem('programmation', 'Programmation', {
      items: [
        ...leaves('programmation', [
          'Planning régie', 'Liste détaillée', 'Calendrier par lieux', 'Line up (artistes par scènes)',
          'Programmes', 'Mes exports ICS', 'Caisses',
        ]),
        menuItem('programmation-calendrier', 'Calendrier par dates', {
          items: leaves('programmation-calendrier', ['Organisé par semaines', 'Organisé par mois', 'Organisé par années']),
        }),
        ...leaves('programmation-parametres', [
          'Organisateurs / Pôles', 'Types', 'Styles', 'Séries', 'Publics cibles', 'Gammes de tarifs',
          'Lieux, scènes, sites, salles',
        ], { setting: true }),
      ],
    }),
    menuItem('programmation-2', 'Liste détaillée'),
  ],
};

export { menuItem as mockMenuItem };
